'use client';

import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

type DelayTimeType = 0 | 100 | 200 | 500 | 1000 | 5000;

export type HoverProps = {
  hoverNode: ReactNode;
  showNode: ReactNode;
  cls?: string;
  qa?: string;
  delayToCloseInMilliseconds?: DelayTimeType;
  delayToOpenInMilliseconds?: DelayTimeType;
  id?: string;
  // Pass both to control the open state from outside; hover then only requests changes
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
};

export const Hover = ({
  hoverNode,
  showNode,
  cls,
  qa,
  delayToCloseInMilliseconds = 500,
  delayToOpenInMilliseconds = 0,
  id,
  isOpen,
  onOpenChange,
}: HoverProps) => {
  const [shouldShowNode, setShouldShowNode] = useState(false);
  const [focus, setFocus] = useState(false);
  const ref = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isControlled = isOpen !== undefined;
  const isShown = isControlled ? isOpen : shouldShowNode;

  const setIsShown = useCallback(
    (nextIsShown: boolean) => {
      if (!isControlled) {
        setShouldShowNode(nextIsShown);
      }

      onOpenChange?.(nextIsShown);
    },
    [isControlled, onOpenChange],
  );

  useEffect(() => {
    const checkClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsShown(false);
        setFocus(false);
      }
    };
    document.addEventListener('mousedown', checkClickOutside);

    return () => document.removeEventListener('mousedown', checkClickOutside);
  }, [setIsShown]);

  useEffect(() => {
    if (!isControlled || isOpen || !ref.current) {
      return;
    }

    clearTimeout(ref.current);
    ref.current = null;
  }, [isControlled, isOpen]);

  const onMouseEnter = () => {
    if (ref.current) {
      clearTimeout(ref.current);
      ref.current = null;
    }

    if (delayToOpenInMilliseconds > 0) {
      ref.current = setTimeout(() => {
        setIsShown(true);
      }, delayToOpenInMilliseconds);
    } else {
      setIsShown(true);
    }
  };

  const onMouseLeave = () => {
    if (ref.current) {
      clearTimeout(ref.current);
    }

    if (!focus) {
      if (delayToCloseInMilliseconds > 0) {
        ref.current = setTimeout(() => {
          setIsShown(false);
        }, delayToCloseInMilliseconds);
      } else {
        setIsShown(false);
      }
    }
  };

  const onFocus = () => {
    setFocus(true);
  };

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cls}
      ref={containerRef}
      data-qa={qa}
      id={id}
    >
      {hoverNode}

      <div
        onFocus={onFocus}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {isShown && showNode}
      </div>
    </div>
  );
};
